import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Plus, Edit, Trash2, Loader2, ArrowLeft, Save, X, Building, ChevronUp, ChevronDown } from 'lucide-react';
import { toast } from 'sonner';
import { adminSponsorsAPI } from '../../services/adminApi';
import ImageUploader from '../../components/ImageUploader';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || '';

const getImageUrl = (url) => {
  if (!url) return null;
  if (url.startsWith('http')) return url;
  if (url.startsWith('/api')) return `${API_BASE_URL}${url}`;
  return `${API_BASE_URL}/api/uploads/image/${url}`;
};

const tierColors = {
  gold: { bg: 'rgba(251, 191, 36, 0.2)', text: '#FBBF24', label: 'Gold' },
  silver: { bg: 'rgba(148, 163, 184, 0.2)', text: '#94A3B8', label: 'Silver' },
  bronze: { bg: 'rgba(217, 119, 6, 0.2)', text: '#D97706', label: 'Bronze' },
  standard: { bg: 'rgba(0, 240, 255, 0.2)', text: '#00F0FF', label: 'Standard' }
};

// List Component
export const AdminSponsors = () => {
  const [sponsors, setSponsors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [reordering, setReordering] = useState(null);

  const fetchSponsors = async () => {
    try {
      const data = await adminSponsorsAPI.getAll();
      setSponsors(data);
    } catch (err) {
      toast.error('Sponsorlar yüklenemedi');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSponsors();
  }, []);

  const handleDelete = async (id) => {
    if (!window.confirm('Bu sponsor silinecek. Emin misiniz?')) return;
    
    try {
      await adminSponsorsAPI.delete(id);
      toast.success('Sponsor silindi');
      fetchSponsors();
    } catch (err) {
      toast.error('Silme işlemi başarısız');
    }
  };

  const handleReorder = async (id, direction) => {
    setReordering(id);
    try {
      await adminSponsorsAPI.reorder(id, direction);
      await fetchSponsors();
    } catch (err) {
      toast.error('Sıralama değiştirilemedi');
    } finally {
      setReordering(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin" style={{ color: '#00F0FF' }} />
      </div>
    );
  }

  return (
    <div data-testid="admin-sponsors">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold" style={{ color: '#F8FAFC' }}>Sponsorlar</h1>
        <Link
          to="/admin/sponsors/new"
          className="flex items-center gap-2 px-4 py-2 rounded-lg transition-all"
          style={{
            background: 'linear-gradient(90deg, #00F0FF 0%, #0F172A 100%)',
            color: '#F8FAFC',
            border: '2px solid #00F0FF',
          }}
        >
          <Plus size={18} />
          Yeni Sponsor
        </Link>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
        {sponsors.map((sponsor, index) => {
          const tier = tierColors[sponsor.tier] || tierColors.standard;
          return (
            <div key={sponsor.id} className="glass-card p-6">
              {/* Reorder buttons */}
              <div className="flex justify-end gap-1 mb-3">
                <button
                  onClick={() => handleReorder(sponsor.id, 'up')}
                  disabled={index === 0 || reordering === sponsor.id}
                  className="p-1.5 rounded transition-colors disabled:opacity-30"
                  style={{ color: '#00F0FF', background: 'rgba(0, 240, 255, 0.1)' }}
                  data-testid={`reorder-up-${sponsor.id}`}
                >
                  <ChevronUp size={16} />
                </button>
                <button
                  onClick={() => handleReorder(sponsor.id, 'down')}
                  disabled={index === sponsors.length - 1 || reordering === sponsor.id}
                  className="p-1.5 rounded transition-colors disabled:opacity-30"
                  style={{ color: '#00F0FF', background: 'rgba(0, 240, 255, 0.1)' }}
                  data-testid={`reorder-down-${sponsor.id}`}
                >
                  <ChevronDown size={16} />
                </button>
              </div>
              
              <div className="h-24 flex items-center justify-center mb-4 rounded-lg overflow-hidden" style={{
                background: 'rgba(255, 255, 255, 0.05)'
              }}>
                {sponsor.logo ? (
                  <img 
                    src={getImageUrl(sponsor.logo)} 
                    alt={sponsor.name} 
                    className="max-w-full max-h-full object-contain p-2"
                    onError={(e) => {
                      e.target.style.display = 'none';
                      e.target.parentElement.innerHTML = '<div class="flex items-center justify-center w-full h-full"><svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#64748B" stroke-width="2"><path d="M3 21h18"></path><path d="M5 21V7l8-4v18"></path><path d="M19 21V11l-6-4"></path><path d="M9 9v.01"></path><path d="M9 12v.01"></path><path d="M9 15v.01"></path><path d="M9 18v.01"></path></svg></div>';
                    }}
                  />
                ) : (
                  <Building size={32} style={{ color: '#64748B' }} />
                )}
              </div>
              <h3 className="font-semibold text-center mb-2" style={{ color: '#F8FAFC' }}>{sponsor.name}</h3>
              <div className="flex justify-center mb-4">
                <span className="px-3 py-1 rounded-full text-xs font-medium" style={{
                  background: tier.bg,
                  color: tier.text
                }}>
                  {tier.label}
                </span>
              </div>
              <div className="flex items-center justify-center gap-2 pt-4" style={{ borderTop: '1px solid rgba(0, 240, 255, 0.1)' }}>
                <Link
                  to={`/admin/sponsors/${sponsor.id}`}
                  className="p-2 rounded-lg transition-colors"
                  style={{ color: '#00F0FF', background: 'rgba(0, 240, 255, 0.1)' }}
                >
                  <Edit size={16} />
                </Link>
                <button
                  onClick={() => handleDelete(sponsor.id)}
                  className="p-2 rounded-lg transition-colors"
                  style={{ color: '#EF4444', background: 'rgba(239, 68, 68, 0.1)' }}
                >
                  <Trash2 size={16} />
                </button>
              </div>
            </div>
          );
        })}
      </div>

      {sponsors.length === 0 && (
        <div className="glass-card p-8 text-center" style={{ color: '#94A3B8' }}>
          Henüz sponsor yok
        </div>
      )}
    </div>
  );
};

// Form Component
export const AdminSponsorForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = !!id;
  
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    logo: '',
    website: '',
    tier: 'standard'
  });

  useEffect(() => {
    if (isEdit) {
      const fetchSponsor = async () => {
        try {
          const data = await adminSponsorsAPI.getById(id);
          setFormData({
            name: data.name || '',
            logo: data.logo || '',
            website: data.website || '',
            tier: data.tier || 'standard'
          });
        } catch (err) {
          toast.error('Sponsor yüklenemedi');
          navigate('/admin/sponsors');
        } finally {
          setLoading(false);
        }
      };
      fetchSponsor();
    }
  }, [id, isEdit, navigate]);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleImageChange = (url) => {
    setFormData({ ...formData, logo: url });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      if (isEdit) {
        await adminSponsorsAPI.update(id, formData);
        toast.success('Sponsor güncellendi');
      } else {
        await adminSponsorsAPI.create(formData);
        toast.success('Sponsor oluşturuldu');
      }
      navigate('/admin/sponsors');
    } catch (err) {
      toast.error(err.message || 'İşlem başarısız');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin" style={{ color: '#00F0FF' }} />
      </div>
    );
  }

  return (
    <div data-testid="admin-sponsor-form">
      <div className="flex items-center gap-4 mb-6">
        <button
          onClick={() => navigate('/admin/sponsors')}
          className="p-2 rounded-lg"
          style={{ color: '#94A3B8', background: 'rgba(148, 163, 184, 0.1)' }}
        >
          <ArrowLeft size={20} />
        </button>
        <h1 className="text-2xl font-bold" style={{ color: '#F8FAFC' }}>
          {isEdit ? 'Sponsor Düzenle' : 'Yeni Sponsor'}
        </h1>
      </div>

      <form onSubmit={handleSubmit} className="glass-card p-6 space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {/* Logo Upload */}
          <div>
            <ImageUploader
              value={formData.logo}
              onChange={handleImageChange}
              label="Sponsor Logosu *"
              placeholder="Logo yükleyin"
              aspectRatio="4/3"
            />
          </div>

          {/* Form Fields */}
          <div className="md:col-span-2 space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm mb-2" style={{ color: '#F8FAFC' }}>Sponsor Adı *</label>
                <input
                  type="text"
                  name="name"
                  required
                  value={formData.name}
                  onChange={handleChange}
                  className="w-full px-4 py-3 rounded-lg"
                  style={{
                    background: 'rgba(15, 23, 42, 0.8)',
                    border: '1px solid rgba(0, 240, 255, 0.3)',
                    color: '#F8FAFC'
                  }}
                />
              </div>
              <div>
                <label className="block text-sm mb-2" style={{ color: '#F8FAFC' }}>Seviye</label>
                <select
                  name="tier"
                  value={formData.tier}
                  onChange={handleChange}
                  className="w-full px-4 py-3 rounded-lg"
                  style={{
                    background: 'rgba(15, 23, 42, 0.8)',
                    border: '1px solid rgba(0, 240, 255, 0.3)',
                    color: '#F8FAFC'
                  }}
                >
                  <option value="gold">Gold</option>
                  <option value="silver">Silver</option>
                  <option value="bronze">Bronze</option>
                  <option value="standard">Standard</option>
                </select>
              </div>
            </div>

            <div>
              <label className="block text-sm mb-2" style={{ color: '#F8FAFC' }}>Website URL</label>
              <input
                type="url"
                name="website"
                value={formData.website}
                onChange={handleChange}
                className="w-full px-4 py-3 rounded-lg"
                style={{
                  background: 'rgba(15, 23, 42, 0.8)',
                  border: '1px solid rgba(0, 240, 255, 0.3)',
                  color: '#F8FAFC'
                }}
                placeholder="https://sponsor-website.com"
              />
            </div>
          </div>
        </div>

        <div className="flex items-center gap-4 pt-4" style={{ borderTop: '1px solid rgba(0, 240, 255, 0.1)' }}>
          <button
            type="button"
            onClick={() => navigate('/admin/sponsors')}
            className="px-6 py-3 rounded-lg"
            style={{ color: '#94A3B8', background: 'rgba(148, 163, 184, 0.1)' }}
          >
            <X size={18} className="inline mr-2" />
            İptal
          </button>
          <button
            type="submit"
            disabled={saving}
            className="px-6 py-3 rounded-lg flex items-center gap-2"
            style={{
              background: 'linear-gradient(90deg, #00F0FF 0%, #0F172A 100%)',
              color: '#F8FAFC',
              border: '2px solid #00F0FF',
            }}
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save size={18} />}
            {isEdit ? 'Güncelle' : 'Oluştur'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default AdminSponsors;
